/**
 * AgroTech — Crop Health Reports
 *
 * Aggregates real data from existing endpoints and renders:
 *   • Estate overview — per-field summary table (NDVI mean, anomaly count, treatment count)
 *   • Audit chain timeline — anomaly → Step-4 decision → remediation → efficacy
 *   • Seasonal NDVI comparison — year-on-year per field (from time-series endpoint)
 *   • CSV export of the overview table
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Download, FilePieChart, Shield, Calendar,
  RefreshCw, Activity, TrendingUp, TrendingDown, Minus,
  CheckCircle2, XCircle, Clock, Navigation, ChevronDown, ChevronUp,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { satelliteBoundariesApi, satelliteImageryApi, anomalyApi, reportsApi } from '../../services/api';
import api from '../../services/api';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Boundary {
  id: string;
  name: string;
  area_hectares?: number;
  metadata?: Record<string, any>;
}

interface AnomalyRecord {
  id: string;
  boundary_id: string;
  boundary_name: string;
  anomaly_type: string;
  severity: string;
  status: string;
  change_percentage: number | string;
  detection_date: string;
  resolution_notes?: string;
}

interface ImageryRecord {
  id: string;
  acquisition_date: string;
  indices: { index_type: string; mean_value: number }[];
}

interface FieldSummary {
  boundary: Boundary;
  latestNdvi: number | null;
  ndviTrend: 'up' | 'down' | 'flat' | 'no_data';
  activeAnomalies: number;
  resolvedAnomalies: number;
  totalAnomalies: number;
  treatmentCount: number;
  lastImageDate: string | null;
}

interface AuditEntry {
  id: string;
  fieldName: string;
  anomalyType: string;
  severity: string;
  detectionDate: string;
  step4Decision: string;
  remediationStatus: string;
  resolutionNotes?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function latestNdvi(imagery: ImageryRecord[]): number | null {
  const sorted = [...imagery].sort((a, b) => b.acquisition_date.localeCompare(a.acquisition_date));
  for (const img of sorted) {
    const ndvi = img.indices.find(i => i.index_type === 'NDVI');
    if (ndvi) return Number(ndvi.mean_value);
  }
  return null;
}

function ndviTrend(imagery: ImageryRecord[]): 'up' | 'down' | 'flat' | 'no_data' {
  const sorted = [...imagery]
    .sort((a, b) => b.acquisition_date.localeCompare(a.acquisition_date))
    .slice(0, 6); // last 6 acquisitions
  const vals = sorted
    .map(img => img.indices.find(i => i.index_type === 'NDVI'))
    .filter(Boolean)
    .map(i => Number(i!.mean_value));
  if (vals.length < 2) return 'no_data';
  const delta = vals[0] - vals[vals.length - 1];
  if (Math.abs(delta) < 0.01) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

function parseStep4Decision(notes?: string): string {
  if (!notes) return 'Pending';
  if (notes.includes('[STEP4: Confirmed → Drone inspection scheduled]')) return 'Drone Scheduled';
  if (notes.includes('[STEP4: Confirmed → Ground visit only]'))          return 'Ground Visit';
  if (notes.includes('[STEP4: Rejected]'))                               return 'Rejected';
  if (notes.includes('[STEP4: Deferred]'))                               return 'Deferred';
  if (notes.includes('[STEP4:'))                                         return 'Actioned';
  return 'Pending';
}

/** Generate a CSV string from the FieldSummary array */
function toCSV(rows: FieldSummary[], areaLabel: string): string {
  const header = ['Field', `Area (${areaLabel})`, 'Latest NDVI', 'Trend', 'Active Anomalies', 'Resolved', 'Treatments', 'Last Image'].join(',');
  const lines = rows.map(r => [
    `"${r.boundary.name}"`,
    r.boundary.area_hectares?.toFixed(2) ?? '',
    r.latestNdvi !== null ? r.latestNdvi.toFixed(3) : '',
    r.ndviTrend,
    r.activeAnomalies,
    r.resolvedAnomalies,
    r.treatmentCount,
    r.lastImageDate ?? '',
  ].join(','));
  return [header, ...lines].join('\n');
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TrendIcon({ trend }: { trend: FieldSummary['ndviTrend'] }) {
  if (trend === 'up')      return <TrendingUp   className="w-4 h-4 text-emerald-500" />;
  if (trend === 'down')    return <TrendingDown  className="w-4 h-4 text-red-500" />;
  if (trend === 'flat')    return <Minus         className="w-4 h-4 text-gray-400" />;
  return <span className="text-xs text-gray-400">—</span>;
}

function Step4Badge({ decision }: { decision: string }) {
  const colour =
    decision === 'Drone Scheduled' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
    decision === 'Ground Visit'    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
    decision === 'Rejected'        ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' :
    decision === 'Deferred'        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
    'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${colour}`}>
      {decision === 'Drone Scheduled' && <Navigation className="w-3 h-3" />}
      {decision === 'Ground Visit'    && <CheckCircle2 className="w-3 h-3" />}
      {decision === 'Rejected'        && <XCircle className="w-3 h-3" />}
      {decision === 'Deferred'        && <Clock className="w-3 h-3" />}
      {decision}
    </span>
  );
}

interface AuditRowProps {
  entry: AuditEntry;
}
function AuditRow({ entry }: AuditRowProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr
        className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
        onClick={() => setOpen(v => !v)}
      >
        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{entry.fieldName}</td>
        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400 capitalize">{entry.anomalyType.replace(/_/g, ' ')}</td>
        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
          {new Date(entry.detectionDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </td>
        <td className="px-4 py-3"><Step4Badge decision={entry.step4Decision} /></td>
        <td className="px-4 py-3 text-xs capitalize text-gray-500 dark:text-gray-400">
          {entry.remediationStatus.replace(/_/g, ' ')}
        </td>
        <td className="px-4 py-3 text-gray-400">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50 dark:bg-gray-800/40">
          <td colSpan={6} className="px-6 py-3">
            <div className="flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-400">
              <span><span className="font-semibold text-gray-700 dark:text-gray-300">Severity:</span> <span className="capitalize">{entry.severity}</span></span>
              <span><span className="font-semibold text-gray-700 dark:text-gray-300">Status:</span> <span className="capitalize">{entry.step4Decision}</span></span>
              <span><span className="font-semibold text-gray-700 dark:text-gray-300">Remediation:</span> <span className="capitalize">{entry.remediationStatus.replace(/_/g, ' ')}</span></span>
            </div>
            {entry.resolutionNotes && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">{entry.resolutionNotes}</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type ActiveTab = 'overview' | 'audit' | 'seasonal';

export const CropHealthReportsPage: React.FC = () => {
  const { uom } = useOrgSettings();
  const [tab, setTab] = useState<ActiveTab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fieldSummaries, setFieldSummaries] = useState<FieldSummary[]>([]);
  const [auditEntries, setAuditEntries]     = useState<AuditEntry[]>([]);

  // Seasonal — per-field monthly NDVI averages (current year vs. previous year)
  const [seasonalField, setSeasonalField]   = useState('');
  const [seasonalData, setSeasonalData]     = useState<{ month: string; current: number | null; previous: number | null }[]>([]);
  const [seasonalLoading, setSeasonalLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // ── 1. Boundaries ─────────────────────────────────────────────────────
      const bRes = await satelliteBoundariesApi.getAll();
      const boundaries: Boundary[] = bRes.data?.boundaries ?? [];

      // ── 2. All anomalies ──────────────────────────────────────────────────
      const anomalyRes = await anomalyApi.getAnomalies({ vertical: 'agrotech' });
      const allAnomalies: AnomalyRecord[] = anomalyRes.anomalies ?? [];

      // ── 3. Remediation missions for treatment count ────────────────────────
      const missionsRes = await api.get('/missions', { params: { limit: 200 } });
      const allMissions = (missionsRes.data.data?.missions ?? missionsRes.data.data ?? []) as any[];
      const remMissions = allMissions.filter((m: any) => m.mission_type === 'remediation');

      // ── 4. Build per-field summaries ──────────────────────────────────────
      const summaries = await Promise.all(
        boundaries.map(async (b): Promise<FieldSummary> => {
          let imagery: ImageryRecord[] = [];
          try {
            const imgRes = await satelliteImageryApi.getImagery(b.id, { limit: 20 });
            imagery = imgRes.imagery ?? [];
          } catch { /* no imagery yet */ }

          const fieldAnomalies = allAnomalies.filter((a: AnomalyRecord) => a.boundary_id === b.id);
          const active   = fieldAnomalies.filter((a: AnomalyRecord) => a.status === 'pending' || a.status === 'investigating').length;
          const resolved = fieldAnomalies.filter((a: AnomalyRecord) => a.status === 'resolved' || a.status === 'false_positive').length;

          const treatments = remMissions.filter(
            (m: any) => m.metadata?.boundary_id === b.id && m.status !== 'draft',
          ).length;

          const sortedDates = [...imagery]
            .sort((a, b2) => b2.acquisition_date.localeCompare(a.acquisition_date));
          const lastImageDate = sortedDates[0]?.acquisition_date?.slice(0, 10) ?? null;

          return {
            boundary: b,
            latestNdvi: latestNdvi(imagery),
            ndviTrend: ndviTrend(imagery),
            activeAnomalies: active,
            resolvedAnomalies: resolved,
            totalAnomalies: fieldAnomalies.length,
            treatmentCount: treatments,
            lastImageDate,
          };
        }),
      );
      setFieldSummaries(summaries);

      // ── 5. Audit chain entries ────────────────────────────────────────────
      const entries: AuditEntry[] = allAnomalies.map((a: AnomalyRecord) => {
        const linkedMission = remMissions.find((m: any) => m.metadata?.anomaly_id === a.id);
        return {
          id: a.id,
          fieldName: a.boundary_name,
          anomalyType: a.anomaly_type ?? 'Unknown',
          severity: a.severity ?? 'unknown',
          detectionDate: a.detection_date,
          step4Decision: parseStep4Decision(a.resolution_notes),
          remediationStatus: linkedMission?.status ?? 'none',
          resolutionNotes: a.resolution_notes,
        };
      });
      setAuditEntries(entries);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-select first field for seasonal tab
  useEffect(() => {
    if (fieldSummaries.length > 0 && !seasonalField) {
      setSeasonalField(fieldSummaries[0].boundary.id);
    }
  }, [fieldSummaries, seasonalField]);

  // ── Seasonal data loader ──────────────────────────────────────────────────
  const loadSeasonal = useCallback(async (fieldId: string) => {
    if (!fieldId) return;
    setSeasonalLoading(true);
    try {
      const now = new Date();
      const currentYear = now.getFullYear();
      const prevYear    = currentYear - 1;

      const [currRes, prevRes] = await Promise.all([
        satelliteImageryApi.getTimeSeries(fieldId, {
          indexType: 'NDVI',
          startDate: `${currentYear}-01-01`,
          endDate:   `${currentYear}-12-31`,
        }),
        satelliteImageryApi.getTimeSeries(fieldId, {
          indexType: 'NDVI',
          startDate: `${prevYear}-01-01`,
          endDate:   `${prevYear}-12-31`,
        }),
      ]);

      type TsPoint = { acquisition_date: string; mean_value: number };

      const toMonthly = (points: TsPoint[]): Record<number, number[]> => {
        const map: Record<number, number[]> = {};
        for (const p of points) {
          const m = new Date(p.acquisition_date).getMonth(); // 0-based
          if (!map[m]) map[m] = [];
          map[m].push(Number(p.mean_value));
        }
        return map;
      };

      const currPoints: TsPoint[] = currRes.timeSeries ?? [];
      const prevPoints: TsPoint[] = prevRes.timeSeries ?? [];
      const currMonthly = toMonthly(currPoints);
      const prevMonthly = toMonthly(prevPoints);

      const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const rows = MONTHS.map((month, i) => {
        const avg = (arr: number[] | undefined) =>
          arr && arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
        return {
          month,
          current:  avg(currMonthly[i]),
          previous: avg(prevMonthly[i]),
        };
      });
      setSeasonalData(rows);
    } catch {
      setSeasonalData([]);
    } finally {
      setSeasonalLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'seasonal' && seasonalField) loadSeasonal(seasonalField);
  }, [tab, seasonalField, loadSeasonal]);

  // ── CSV export ────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const csv = toCSV(fieldSummaries, uom.area_unit_label);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `crop-health-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Summary stats ─────────────────────────────────────────────────────────
  const totalFields     = fieldSummaries.length;
  const totalActive     = fieldSummaries.reduce((s, f) => s + f.activeAnomalies, 0);
  const totalTreatments = fieldSummaries.reduce((s, f) => s + f.treatmentCount, 0);
  const avgNdvi = (() => {
    const vals = fieldSummaries.map(f => f.latestNdvi).filter((v): v is number => v !== null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  })();

  const SELECT_CLS = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText className="w-7 h-7 text-sky-600" /> Crop Health Reports
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Estate-wide field health, audit chain, and seasonal NDVI comparison
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {tab === 'overview' && !loading && fieldSummaries.length > 0 && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  const firstBoundaryId = fieldSummaries[0]?.boundary?.id;
                  if (firstBoundaryId) {
                    reportsApi.downloadCertifiedPdf('carbon_credit', firstBoundaryId);
                  }
                }}
              >
                <Download className="w-4 h-4 mr-1.5" />Certified PDF Report
              </Button>
              <Button size="sm" variant="outline" onClick={handleExportCSV}>
                <Download className="w-4 h-4 mr-1.5" />Export CSV
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ── Error ── */}
      {error && <Alert type="error">{error}</Alert>}

      {/* ── Estate stats ── */}
      {!loading && fieldSummaries.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Fields Monitored', value: totalFields,    colour: 'text-sky-600' },
            { label: 'Active Anomalies', value: totalActive,    colour: totalActive > 0 ? 'text-amber-600' : 'text-green-600' },
            { label: 'Treatments Run',   value: totalTreatments,colour: 'text-cyan-600' },
            { label: 'Estate Avg NDVI',  value: avgNdvi !== null ? avgNdvi.toFixed(3) : '—', colour: avgNdvi !== null && avgNdvi >= 0.5 ? 'text-emerald-600' : 'text-amber-600' },
          ].map(({ label, value, colour }) => (
            <Card key={label} className="p-4 text-center">
              <p className={`text-2xl font-bold ${colour}`}>{value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
        {([
          ['overview',  <FilePieChart className="w-4 h-4" />, 'Field Health Overview'],
          ['audit',     <Shield className="w-4 h-4" />,       'Audit Chain'],
          ['seasonal',  <Calendar className="w-4 h-4" />,     'Seasonal Comparison'],
        ] as const).map(([key, icon, label]) => (
          <button
            key={key}
            onClick={() => setTab(key as ActiveTab)}
            className={`flex items-center gap-2 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
              tab === key
                ? 'border-sky-500 text-sky-600 dark:text-sky-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {icon}{label}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      {loading ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : fieldSummaries.length === 0 ? (
        <Card>
          <div className="text-center py-10">
            <Activity className="mx-auto w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No fields found</p>
            <p className="text-xs text-gray-400 mt-1">Add fields in the Fields page and fetch satellite imagery to populate this report.</p>
          </div>
        </Card>
      ) : tab === 'overview' ? (

        /* ── Overview table ── */
        <Card padding="none">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{fieldSummaries.length} field{fieldSummaries.length !== 1 ? 's' : ''}</p>
            <p className="text-xs text-gray-400">Latest NDVI mean · Trend based on last 6 acquisitions</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm divide-y divide-gray-100 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Field', `Area (${uom.area_unit_label})`, 'Latest NDVI', 'Trend', 'Active Anomalies', 'Resolved', 'Treatments', 'Last Image'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {fieldSummaries.map(f => (
                  <tr key={f.boundary.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">{f.boundary.name}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{f.boundary.area_hectares?.toFixed(1) ?? '—'}</td>
                    <td className="px-4 py-3 font-mono font-bold text-gray-900 dark:text-white">
                      {f.latestNdvi !== null ? (
                        <span className={f.latestNdvi >= 0.5 ? 'text-emerald-600 dark:text-emerald-400' : f.latestNdvi >= 0.3 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}>
                          {f.latestNdvi.toFixed(3)}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3"><TrendIcon trend={f.ndviTrend} /></td>
                    <td className="px-4 py-3">
                      {f.activeAnomalies > 0
                        ? <span className="inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">{f.activeAnomalies}</span>
                        : <span className="text-gray-400 text-xs">0</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">{f.resolvedAnomalies}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">{f.treatmentCount}</td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                      {f.lastImageDate
                        ? new Date(f.lastImageDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

      ) : tab === 'audit' ? (

        /* ── Audit chain table ── */
        <Card padding="none">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{auditEntries.length} anomaly event{auditEntries.length !== 1 ? 's' : ''}</p>
            <p className="text-xs text-gray-400">Anomaly → Step-4 decision → remediation status</p>
          </div>
          {auditEntries.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="mx-auto w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No anomalies recorded yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm divide-y divide-gray-100 dark:divide-gray-800">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {['Field', 'Anomaly Type', 'Detected', 'Step-4 Decision', 'Remediation', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {auditEntries.map(e => <AuditRow key={e.id} entry={e} />)}
                </tbody>
              </table>
            </div>
          )}
        </Card>

      ) : (

        /* ── Seasonal comparison ── */
        <div className="space-y-4">
          <Card>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Field:</label>
                <select
                  value={seasonalField}
                  onChange={e => { setSeasonalField(e.target.value); }}
                  className={SELECT_CLS}
                >
                  {fieldSummaries.map(f => (
                    <option key={f.boundary.id} value={f.boundary.id}>{f.boundary.name}</option>
                  ))}
                </select>
              </div>
              <Button size="sm" variant="outline" onClick={() => loadSeasonal(seasonalField)} disabled={seasonalLoading}>
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${seasonalLoading ? 'animate-spin' : ''}`} />
                Load
              </Button>
            </div>
          </Card>

          {seasonalLoading ? (
            <div className="flex justify-center py-12"><Spinner size="lg" /></div>
          ) : seasonalData.length === 0 ? (
            <Card className="p-8 text-center">
              <Calendar className="mx-auto w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No seasonal data available for this field</p>
              <p className="text-xs text-gray-400 mt-1">Fetch satellite imagery for this field to see year-on-year comparison</p>
            </Card>
          ) : (
            <Card padding="none">
              <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Monthly NDVI — {fieldSummaries.find(f => f.boundary.id === seasonalField)?.boundary.name}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">Current year vs. previous year · Mean per month</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Month</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">This Year</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Prev Year</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">YoY Change</th>
                      <th className="px-4 py-3 w-40 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Comparison</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {seasonalData.map(row => {
                      const yoy = row.current !== null && row.previous !== null
                        ? row.current - row.previous
                        : null;
                      const barMax = 0.9;
                      const pctCurr  = row.current  !== null ? Math.round((row.current  / barMax) * 100) : 0;
                      const pctPrev  = row.previous !== null ? Math.round((row.previous / barMax) * 100) : 0;
                      return (
                        <tr key={row.month} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                          <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">{row.month}</td>
                          <td className="px-4 py-3 font-mono text-emerald-700 dark:text-emerald-400 font-semibold">
                            {row.current !== null ? row.current.toFixed(3) : '—'}
                          </td>
                          <td className="px-4 py-3 font-mono text-gray-500 dark:text-gray-400">
                            {row.previous !== null ? row.previous.toFixed(3) : '—'}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">
                            {yoy !== null ? (
                              <span className={yoy >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                                {yoy >= 0 ? '+' : ''}{yoy.toFixed(3)}
                              </span>
                            ) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              {row.current !== null && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] text-emerald-600 w-4">CY</span>
                                  <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(pctCurr, 100)}%` }} />
                                  </div>
                                </div>
                              )}
                              {row.previous !== null && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] text-gray-400 w-4">PY</span>
                                  <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                    <div className="h-full rounded-full bg-gray-400" style={{ width: `${Math.min(pctPrev, 100)}%` }} />
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default CropHealthReportsPage;
// Made with Bob
